/**
 * The Tixologi API client of the native show surface: downstroke's port of tixologist
 * `src/services/api.ts` (snapshot 2f0c9253), with the same exports, signatures and return
 * shapes, so a ported call site compiles unchanged and still reads `error.response.data`
 * and `.status` off the original AxiosError.
 *
 * Where it differs from the iframe's client:
 * - **Where a path goes.** Every path gets one leading slash (tixologist call sites write
 *   both `events/1` and `/events/1`) and is routed by `tixologiUrl()` to the service that
 *   owns it, instead of sharing one `baseURL`.
 * - **Whose token.** The bearer is the token of the scope `TixologiScopeProvider`
 *   registered (`src/scope-registry.ts`). A request keeps the scope it started with, so its
 *   retry uses the same venue's token even after the page moved to another.
 * - **When to refresh.** Before sending, when the token is within 300 s of its `exp`.
 *   After a rejection whose body names the token, always (as tixologist refreshed); after a
 *   bare 401 only when the token the request carried is itself expired or about to be: the
 *   backend's Allow() middleware also answers 401 for a role or partner mismatch, which a
 *   new token cannot fix. Concurrent refreshes of one scope share one login, and a request
 *   is retried at most once.
 * - **Never signs anyone out.** No storage clearing, redirect or `logout` message to a
 *   parent frame: a failed refresh rejects with the request's original error.
 *
 * The rule "never fetch on the client; use getApiClient()" governs Punchup's own API.
 * Tixologi is an external API authenticated by a per-venue bearer, which getApiClient()
 * cannot address (apps/web/hooks/tixologi/tags/useTags.ts records the same exemption).
 */
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

import { tixologiUrl } from "./api-base";
import { INVALID_TOKEN, UNAVAILABLE_TOKEN } from "./constants";
import {
  getCurrentTixologiScope,
  type RegisteredTixologiScope,
} from "./scope-registry";
import { isTokenExpiringSoon } from "./utils/jwt";

export {
  getCurrentTixologiScope,
  registerTixologiScope,
} from "./scope-registry";
export type {
  RegisteredTixologiScope,
  TixologiScopeSource,
} from "./scope-registry";

export const api = axios.create({
  headers: { Accept: "*/*", "Content-Type": "application/json" },
});

/** What a request carries from its first send to its one retry. */
type TixologiRequest = InternalAxiosRequestConfig & {
  tixologiScope?: RegisteredTixologiScope;
  tixologiToken?: string | null;
  _retry?: boolean;
};

/** Already absolute: a retry (routed on its first send) or a caller's full URL. */
const ABSOLUTE_URL = /^[a-z][a-z\d+\-.]*:\/\//i;

/** Tixologi's own sign-in paths, which are never sent a bearer nor refreshed. */
const SIGN_IN_PATH = /(^|\/)(login|signup)(\/|\?|$)/;

const withLeadingSlash = (url: string): string => `/${url.replace(/^\/+/, "")}`;

api.interceptors.request.use(async (config) => {
  const request = config as TixologiRequest;

  let url = request.url ?? "";
  if (!ABSOLUTE_URL.test(url)) {
    url = tixologiUrl(withLeadingSlash(url));
    request.url = url;
    request.baseURL = undefined;
  }

  // TIC-333: under a JSON Content-Type, axios serialises a FormData body to JSON. Unset,
  // the browser sends it as multipart/form-data with its boundary.
  if (typeof FormData !== "undefined" && request.data instanceof FormData) {
    request.headers.delete("Content-Type");
  }

  if (SIGN_IN_PATH.test(url)) return request;

  request.tixologiScope ??= getCurrentTixologiScope() ?? undefined;
  const scope = request.tixologiScope;
  if (!scope) return request;

  let token = request.tixologiToken ?? scope.getToken();
  if (token && !request._retry && isTokenExpiringSoon(token)) {
    // A failed refresh sends the old token anyway: it may have minutes left, and if it
    // has none the 401 below tries once more.
    token = (await scope.refresh()) ?? token;
  }

  request.tixologiToken = token;
  if (token) request.headers.set("Authorization", `Bearer ${token}`);
  return request;
});

/** The body's `error` names the token: Firebase's invalid-token tail, or "Token not available". */
function bodyNamesToken(error: AxiosError): boolean {
  const data: unknown = error.response?.data;
  const message =
    typeof data === "object" && data !== null
      ? (data as { error?: unknown }).error
      : undefined;
  return (
    typeof message === "string" &&
    (message.includes(INVALID_TOKEN) || message === UNAVAILABLE_TOKEN)
  );
}

/**
 * Whether a fresh token can answer the rejection: always when the body names the token;
 * after a bare 401 only when the token the request carried is expired or about to be, since
 * the backend answers 401 for a role or partner mismatch too.
 */
function refreshCanAnswer(
  error: AxiosError,
  token: string | null | undefined
): boolean {
  if (bodyNamesToken(error)) return true;
  return error.response?.status === 401 && isTokenExpiringSoon(token);
}

api.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error) || !error.config) throw error;

    const request = error.config as TixologiRequest;
    const scope = request.tixologiScope;
    if (
      !scope ||
      request._retry ||
      !refreshCanAnswer(error, request.tixologiToken)
    ) {
      throw error;
    }

    request._retry = true;
    const token = await scope.refresh();
    if (!token) throw error;

    request.tixologiToken = token;
    return api.request(request);
  }
);

// The helpers below default `T` to `any` on purpose: they mirror tixologist's untyped
// ones, and the ported call sites read their results without a type argument. New code
// passes `T`.

/** GETs `endpoint` and resolves the response body. */
export const getData = async <T = any>(
  endpoint: string,
  options?: AxiosRequestConfig
): Promise<T> => {
  const { data } = await api.get<T>(endpoint, options);
  return data;
};

/** GETs `endpoint` and resolves the whole response (headers included). */
export const getResponse = <T = any>(
  endpoint: string,
  options: AxiosRequestConfig = {}
): Promise<AxiosResponse<T>> => api.get<T>(endpoint, options);

/** POSTs `body` (tixologist called it `options`) and resolves the response body. */
export const post = async <T = any>(
  endpoint: string,
  body: unknown = {}
): Promise<T> => {
  const { data } = await api.post<T>(endpoint, body);
  return data;
};

/** PUTs `body` and resolves the response body. */
export const put = async <T = any>(
  endpoint: string,
  body: unknown = {}
): Promise<T> => {
  const { data } = await api.put<T>(endpoint, body);
  return data;
};

/** DELETEs `endpoint` (`options` is the request config) and resolves the response body. */
export const deleteData = async <T = any>(
  endpoint: string,
  options: AxiosRequestConfig = {}
): Promise<T> => {
  const { data } = await api.delete<T>(endpoint, options);
  return data;
};

export default api;
